"use client";

import { useState, useEffect } from "react";
import { Trash2, Pencil, Home, Tv } from "lucide-react";
import { EditClassifiedModal } from "./EditClassifieldModal";
import { successToast, errorToast } from "../../utils/toasters";
import { deleteClassified, updateClassified } from "../../services/classified";

function ManageClassifieds({ classifieds: initialClassifieds }) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClassified, setSelectedClassified] = useState(null);
  const [originalClassifiedData, setOriginalClassifiedData] = useState(null);
  const [localClassifieds, setLocalClassifieds] = useState(
    initialClassifieds || []
  );
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const handleDeleteClassifiedItem = async (classifiedId) => {
    const originalClassifieds = [...localClassifieds];
    try {
      setLocalClassifieds((prevClassifieds) =>
        prevClassifieds.filter((item) => item.id !== classifiedId)
      );
      const response = await deleteClassified(classifiedId);
      if (response.status === "sucess" || response.status === "success") {
        successToast("You’ve successfully deleted the listing", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      } else {
        errorToast("Failed to delete the listing. Please try again.");
      }
    } catch (error) {
      setLocalClassifieds(originalClassifieds);
      errorToast("Some thing went wrong!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
      });
    } finally {
      setIsDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const onClassifiedUpdate = async (classifiedId, updatedData) => {
    const originalClassifieds = [...localClassifieds];

    try {
      const dataToProcess = updatedData.formattedUpdate || updatedData;

      if (updatedData.noChanges) {
        successToast("No changes made to the listing.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
        });
        return;
      }

      if (updatedData.fullData) {
        setLocalClassifieds((prevClassifieds) =>
          prevClassifieds.map((item) =>
            item.id === classifiedId
              ? { ...item, ...updatedData.fullData }
              : item
          )
        );
      }

      const formData = new FormData();

      if (dataToProcess.details && Array.isArray(dataToProcess.details)) {
        dataToProcess.details.forEach((detail) => {
          const formattedValue = `${detail.key}:${detail.value}`;
          formData.append("specific_details", formattedValue);
        });
      }

      const response = await updateClassified(classifiedId, formData);

      if (response.status === "success" || response.status === "sucess") {
        successToast("Listing updated successfully", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      } else {
        setLocalClassifieds(originalClassifieds);
        errorToast("Failed to update the listing. Please try again.");
      }
    } catch (error) {
      setLocalClassifieds(originalClassifieds);
      errorToast("Something went wrong during update!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
      });
    }
  };

  useEffect(() => {
    setLocalClassifieds(initialClassifieds || []);
  }, [initialClassifieds]);

  // Helper function to deep compare values
  const deepEqual = (a, b) => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, index) => val === sortedB[index]);
    }
    if (typeof a === "object" && typeof b === "object") {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every((key) => deepEqual(a[key], b[key]));
    }
    return false;
  };

  // Helper function to convert details array to object for easier comparison
  const convertDetailsToObject = (detailsArray) => {
    if (!Array.isArray(detailsArray)) return {};

    const detailsObj = {};
    detailsArray.forEach((detail) => {
      if (detail.key && detail.value !== undefined) {
        // Handle special cases for boolean and array values
        if (detail.key === "isUtilities" || detail.key === "isPreowned") {
          detailsObj[detail.key] =
            detail.value === "true" || detail.value === true;
        } else if (detail.key === "amenities") {
          detailsObj[detail.key] =
            typeof detail.value === "string"
              ? detail.value.split(",").filter(Boolean)
              : detail.value;
        } else if (
          detail.key === "condition" ||
          detail.key === "bedrooms" ||
          detail.key === "bathrooms" ||
          detail.key === "squareFoot" ||
          detail.key === "totalLeaseLength" ||
          detail.key === "remainingLease" ||
          detail.key === "kilometers"
        ) {
          detailsObj[detail.key] = Number(detail.value);
        } else {
          detailsObj[detail.key] = detail.value;
        }
      }
    });
    return detailsObj;
  };

  // Helper function to format changed fields in original data structure format
  const formatChangedFieldsForConsole = (original, updated, changedFields) => {
    const formattedUpdate = {};

    // Handle basic field changes (though these are read-only in edit form)
    const basicFields = [
      "title",
      "description",
      "reasonToSell",
      "price",
      "addressLine1",
      "addressLine2",
      "state",
    ];
    basicFields.forEach((field) => {
      if (changedFields[field]) {
        formattedUpdate[field] = changedFields[field].to;
      }
    });

    // Handle details changes - format them as array like original structure
    const detailChanges = {};
    const originalDetails = original?.details
      ? convertDetailsToObject(original.details)
      : {};
    const updatedDetails = updated?.details
      ? convertDetailsToObject(updated.details)
      : {};

    // Collect all detail field changes
    Object.keys(changedFields).forEach((key) => {
      // Skip basic fields as they're handled above
      if (!basicFields.includes(key)) {
        detailChanges[key] = changedFields[key].to;
      }
    });

    // If there are detail changes, format them properly
    if (Object.keys(detailChanges).length > 0) {
      const detailsArray = [];

      Object.entries(detailChanges).forEach(([key, value]) => {
        // Find the original detail entry to preserve id and listing_id if available
        const originalDetailEntry = original?.details?.find(
          (detail) => detail.key === key
        );

        let formattedValue = value;

        // Format value based on type
        if (key === "amenities" && Array.isArray(value)) {
          formattedValue = value.join(",");
        } else if (typeof value === "boolean") {
          formattedValue = value.toString();
        } else if (typeof value === "number") {
          formattedValue = value.toString();
        }

        detailsArray.push({
          id: originalDetailEntry?.id || null,
          listing_id:
            originalDetailEntry?.listing_id ||
            updated.id ||
            selectedClassified?.id,
          key: key,
          value: formattedValue,
        });
      });

      if (detailsArray.length > 0) {
        formattedUpdate.details = detailsArray;
      }
    }

    return formattedUpdate;
  };

  // Helper function to get only changed fields
  const getChangedFields = (original, updated) => {
    const changes = {};

    // Compare basic fields (though these are read-only in the edit form)
    const basicFields = [
      "title",
      "description",
      "reasonToSell",
      "price",
      "addressLine1",
      "addressLine2",
      "state",
    ];
    basicFields.forEach((field) => {
      if (original[field] !== updated[field]) {
        changes[field] = {
          from: original[field],
          to: updated[field],
        };
      }
    });

    // Compare specific details (the main editable fields)
    const originalDetails = original?.details
      ? convertDetailsToObject(original.details)
      : {};
    const updatedDetails = updated?.details
      ? convertDetailsToObject(updated.details)
      : {};

    // Check each field in updated details
    Object.keys(updatedDetails).forEach((key) => {
      const updatedValue = updatedDetails[key];
      const originalValue = originalDetails[key];

      if (!deepEqual(updatedValue, originalValue)) {
        changes[key] = {
          from: originalValue,
          to: updatedValue,
        };
      }
    });

    // Check for removed fields
    Object.keys(originalDetails).forEach((key) => {
      if (!(key in updatedDetails)) {
        changes[key] = {
          from: originalDetails[key],
          to: null,
        };
      }
    });

    return changes;
  };

  // Helper function to get the appropriate icon based on type
  const getTypeIcon = (type) => {
    switch (type.toLowerCase()) {
      case "house":
        return <Home className="mr-2" size={20} />;
      case "appliance":
        return <Tv className="mr-2" size={20} />;
      default:
        return null;
    }
  };

  const handleEditClick = (classified) => {
    // Store the original data for comparison
    setOriginalClassifiedData(JSON.parse(JSON.stringify(classified))); // Deep copy
    // Set the selected classified
    setSelectedClassified(classified);
    // Open the modal
    setTimeout(() => {
      setIsEditModalOpen(true);
    }, 0);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedClassified(null);
    setOriginalClassifiedData(null);
  };

  const handleEditSave = (updatedData) => {
    // Compare original vs updated data to get only changed fields
    if (originalClassifiedData) {
      const changedFields = getChangedFields(
        originalClassifiedData,
        updatedData
      );

      if (Object.keys(changedFields).length > 0) {
        // Format the changes in the same structure as original data
        const formattedUpdate = formatChangedFieldsForConsole(
          originalClassifiedData,
          updatedData,
          changedFields
        );

        // Call the parent component's update handler with only changed fields
        if (onClassifiedUpdate && selectedClassified) {
          onClassifiedUpdate(selectedClassified.id, {
            id: selectedClassified.id,
            changes: changedFields,
            formattedUpdate: formattedUpdate, // The formatted update for API
            fullData: updatedData, // Include full data in case parent needs it
          });
        }

        // Update local state with full updated data
        setLocalClassifieds((prev) =>
          prev.map((item) =>
            item.id === selectedClassified?.id
              ? { ...item, ...updatedData }
              : item
          )
        );
      } else {
        if (onClassifiedUpdate && selectedClassified) {
          onClassifiedUpdate(selectedClassified.id, { noChanges: true });
        }
      }
    } else {
      if (onClassifiedUpdate && selectedClassified) {
        onClassifiedUpdate(selectedClassified.id, updatedData);
      }

      setLocalClassifieds((prev) =>
        prev.map((item) =>
          item.id === selectedClassified?.id
            ? { ...item, ...updatedData }
            : item
        )
      );
    }

    // Close the modal
    handleEditModalClose();
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setIsDeleteConfirmOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-6">
      <h1 className="text-2xl font-bold mb-6">Manage Classifieds</h1>

      <div className="w-full">
        {/* Show empty state if no classifieds */}
        {!localClassifieds || localClassifieds.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            You don't have any Classifieds yet.
          </div>
        ) : (
          <>
            {/* Header row */}
            <div className="grid grid-cols-3 bg-gray-100 p-3 rounded-t-md">
              <div className="text-sm text-gray-600">Type</div>
              <div className="text-sm text-gray-600">Item Details</div>
              <div className="text-sm text-gray-600 text-right">Actions</div>
            </div>

            {/* Listing rows */}
            {localClassifieds.map((item) => {
              const detailsMap =
                item.details?.reduce((acc, detail) => {
                  acc[detail.key] = detail.value;
                  return acc;
                }, {}) || {};

              return (
                <div
                  key={item.id}
                  className="grid grid-cols-3 py-4 border-b border-gray-100 items-center"
                >
                  {/* Type column */}
                  <div className="flex items-center">
                    {getTypeIcon(item.category_name)}
                    <span className="font-medium">{item.category_name}</span>
                  </div>

                  {/* Item Details column */}
                  <div className="flex">
                    <div className="w-20 h-20 rounded overflow-hidden mr-4">
                      <img
                        src={
                          item.coverPhoto ||
                          "/placeholder.svg?height=80&width=80" ||
                          "/placeholder.svg" ||
                          "/placeholder.svg"
                        }
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-medium">{item.title}</p>
                      {item.category_name === "House" && (
                        <div className="flex items-center text-gray-500 text-sm mt-1">
                          <span className="mr-4">
                            <span className="inline-block mr-1">🛏️</span>{" "}
                            {detailsMap.bedNo || detailsMap.bedrooms} bed
                          </span>
                          <span>
                            <span className="inline-block mr-1">🚿</span>{" "}
                            {detailsMap.bathNo || detailsMap.bathrooms} bath
                          </span>
                        </div>
                      )}
                      <div className="flex items-center mt-1">
                        <span className="font-bold text-lg">
                          $
                          {typeof item.price === "number"
                            ? item.price.toLocaleString()
                            : item.price}
                        </span>
                        <span className="ml-2 px-2 py-0.5 text-xs bg-gray-800 text-white rounded">
                          {item.condition || detailsMap.condition}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions column */}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEditClick(item)}
                      className="flex items-center gap-1 px-2 py-2 rounded-full border border-gray-300 text-[#7B189F] hover:bg-gray-50 transition-colors"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(item)}
                      className="text-gray-500 hover:text-red-500 p-2 transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Edit Modal - Only render when we have both modal open and selected data */}
      {isEditModalOpen && selectedClassified && (
        <EditClassifiedModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          classifiedId={selectedClassified.id}
          initialData={selectedClassified}
          onSave={handleEditSave}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCancelDelete}
            />

            {/* Dialog */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Delete Classified
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete? This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteClassifiedItem(itemToDelete?.id)}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageClassifieds;
